import { useState } from 'react';
import { Plus, X, Sparkles, Loader2, Zap, MessageCircleQuestion, HelpCircle, ShieldAlert, Target } from 'lucide-react';
import type { PitchBook, PitchBookItem, PitchBookObjection, AboutCompany } from '../../types/settings';
import { generatePitchBookContent } from '../../services/salesMaterialsService';
import SimpleCustomSelect from '../shared/SimpleCustomSelect';

interface PitchBookSectionProps {
  data: PitchBook;
  aboutCompany: AboutCompany;
  onChange: (data: PitchBook) => void;
}

const OBJECTION_CATEGORIES: { value: PitchBookObjection['category']; label: string }[] = [
  { value: 'timing', label: 'Timing' },
  { value: 'budget', label: 'Budget' },
  { value: 'authority', label: 'Authority' },
  { value: 'need', label: 'Need' },
  { value: 'competition', label: 'Competition' },
  { value: 'other', label: 'Other' },
];

function ListSection({
  title,
  description,
  icon: Icon,
  items,
  minItems,
  placeholder,
  sectionKey,
  aboutCompany,
  onAdd,
  onUpdate,
  onRemove,
  onBulkAdd,
}: {
  title: string;
  description: string;
  icon: React.ElementType;
  items: PitchBookItem[];
  minItems: number;
  placeholder: string;
  sectionKey: 'hooks' | 'elaborations' | 'engaging_questions' | 'qualifying_questions';
  aboutCompany: AboutCompany;
  onAdd: () => void;
  onUpdate: (id: string, text: string) => void;
  onRemove: (id: string) => void;
  onBulkAdd: (items: PitchBookItem[]) => void;
}) {
  const [generating, setGenerating] = useState(false);

  const handleGenerateAll = async () => {
    if (!aboutCompany.products_services) return;
    setGenerating(true);
    const count = Math.max(minItems, 3);
    const currentTexts = items.map(i => i.text).filter(t => t.trim().length > 0);
    const results = await generatePitchBookContent(sectionKey, aboutCompany, undefined, count, currentTexts);
    if (results) {
      const newItems: PitchBookItem[] = results.map(text => ({
        id: crypto.randomUUID(),
        text,
        is_ai_generated: true,
      }));
      onBulkAdd(newItems);
    }
    setGenerating(false);
  };

  const hasCompanyInfo = !!aboutCompany.products_services;

  return (
    <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
            <Icon className="w-5 h-5 text-[#1875FF]" />
          </div>
          <div>
            <h3 className="text-lg font-extrabold text-gray-900 font-display">{title}</h3>
            <p className="text-sm text-gray-500">{description} (at least {minItems})</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleGenerateAll}
            disabled={generating || !hasCompanyInfo}
            title={!hasCompanyInfo ? 'Fill in About Company first' : ''}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-lg hover:shadow-md transition-all disabled:opacity-50"
          >
            {generating ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Sparkles className="w-4 h-4" />
            )}
            {generating ? 'Generating...' : 'AI Generate'}
          </button>
          <button
            onClick={onAdd}
            className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#1875FF] bg-[#1875FF]/10 rounded-lg hover:bg-[#1875FF]/20 transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add
          </button>
        </div>
      </div>

      <div className="space-y-3">
        {items.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-4">
            No items yet. Click "Add" or "AI Generate" to get started.
          </p>
        )}
        {items.map((item, index) => (
          <div key={item.id} className="flex items-start gap-3 group">
            <span className="mt-3 text-xs font-medium text-gray-400 w-5 text-right shrink-0">{index + 1}.</span>
            <div className="flex-1 relative">
              <textarea
                value={item.text}
                onChange={(e) => onUpdate(item.id, e.target.value)}
                placeholder={placeholder}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm resize-none"
                rows={2}
              />
              {item.is_ai_generated && (
                <span className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10 text-[#1875FF] rounded-full text-[10px] font-medium">
                  <Sparkles className="w-2.5 h-2.5" />
                  AI
                </span>
              )}
            </div>
            <button
              onClick={() => onRemove(item.id)}
              className="mt-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function PitchBookSection({ data, aboutCompany, onChange }: PitchBookSectionProps) {
  const [generatingObjection, setGeneratingObjection] = useState<string | null>(null);

  const createItem = (): PitchBookItem => ({
    id: crypto.randomUUID(),
    text: '',
    is_ai_generated: false,
  });

  const updateItems = (
    field: 'hooks' | 'elaborations' | 'engaging_questions' | 'qualifying_questions',
    updater: (items: PitchBookItem[]) => PitchBookItem[]
  ) => {
    onChange({ ...data, [field]: updater(data[field]) });
  };

  const addObjection = () => {
    onChange({
      ...data,
      objections: [
        ...data.objections,
        {
          id: crypto.randomUUID(),
          title: '',
          response: '',
          category: 'other' as const,
          is_ai_generated: false,
        },
      ],
    });
  };

  const updateObjection = (id: string, updates: Partial<PitchBookObjection>) => {
    onChange({
      ...data,
      objections: data.objections.map(o => o.id === id ? { ...o, ...updates } : o),
    });
  };

  const removeObjection = (id: string) => {
    onChange({
      ...data,
      objections: data.objections.filter(o => o.id !== id),
    });
  };

  const generateObjectionResponse = async (objectionId: string) => {
    const objection = data.objections.find(o => o.id === objectionId);
    if (!objection?.title || !aboutCompany.products_services) return;

    setGeneratingObjection(objectionId);
    const currentResponse = objection.response?.trim() ? [objection.response] : undefined;
    const results = await generatePitchBookContent(
      'objection_response',
      aboutCompany,
      { title: objection.title, category: objection.category },
      1,
      currentResponse
    );
    if (results && results[0]) {
      updateObjection(objectionId, { response: results[0], is_ai_generated: true });
    }
    setGeneratingObjection(null);
  };

  const generateAllObjections = async () => {
    if (!aboutCompany.products_services) return;
    setGeneratingObjection('all');
    const currentObjectionTexts = data.objections
      .map(o => `${o.title}: ${o.response}`.trim())
      .filter(t => t.length > 1);
    const results = await generatePitchBookContent('objection_response', aboutCompany, undefined, 10, currentObjectionTexts);
    if (results) {
      const validCategories = ['timing', 'budget', 'authority', 'need', 'competition', 'other'] as const;
      const parsed: PitchBookObjection[] = results.map(item => {
        const lines = item.split('\n');
        let category: PitchBookObjection['category'] = 'other';
        let title = '';
        let responseLines: string[] = [];
        let foundResponse = false;

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed) {
            if (foundResponse) responseLines.push('');
            continue;
          }

          const categoryMatch = trimmed.match(/^CATEGORY:\s*(.+)/i);
          if (categoryMatch) {
            const raw = categoryMatch[1].trim().toLowerCase();
            if (validCategories.includes(raw as typeof validCategories[number])) {
              category = raw as PitchBookObjection['category'];
            }
            continue;
          }

          const objectionMatch = trimmed.match(/^OBJECTION:\s*(.+)/i);
          if (objectionMatch) {
            title = objectionMatch[1].replace(/^["']|["']$/g, '').trim();
            continue;
          }

          const responseMatch = trimmed.match(/^RESPONSE:\s*(.+)/i);
          if (responseMatch) {
            foundResponse = true;
            responseLines.push(responseMatch[1].replace(/^["']|["']$/g, '').trim());
            continue;
          }

          if (foundResponse) {
            responseLines.push(trimmed);
          } else if (!title) {
            const legacyMatch = trimmed.match(/^Objection\s*\((\w+)\):\s*(.+)/i);
            if (legacyMatch) {
              const raw = legacyMatch[1].trim().toLowerCase();
              if (validCategories.includes(raw as typeof validCategories[number])) {
                category = raw as PitchBookObjection['category'];
              }
              title = legacyMatch[2].replace(/^["']|["']$/g, '').replace(/["']\s*:\s*["'].*$/, '').trim();
            } else {
              title = trimmed.replace(/^["']|["']$/g, '').trim();
            }
          } else {
            foundResponse = true;
            responseLines.push(trimmed.replace(/^Response:\s*/i, '').replace(/^["']|["']$/g, '').trim());
          }
        }

        if (!title && !responseLines.length) {
          title = item.slice(0, 80);
          responseLines = [item];
        }

        return {
          id: crypto.randomUUID(),
          title,
          response: responseLines.join('\n').trim(),
          category,
          is_ai_generated: true,
        };
      });
      onChange({ ...data, objections: [...data.objections, ...parsed] });
    }
    setGeneratingObjection(null);
  };

  const hasCompanyInfo = !!aboutCompany.products_services;

  return (
    <div className="space-y-8">
      <ListSection
        title="1st Pitch - Hook"
        description="Short, attention-grabbing opening lines"
        icon={Zap}
        items={data.hooks}
        minItems={3}
        placeholder="E.g.: Did you know that 60% of sales teams waste 4+ hours daily on manual outreach that could be automated?"
        sectionKey="hooks"
        aboutCompany={aboutCompany}
        onAdd={() => updateItems('hooks', items => [...items, createItem()])}
        onUpdate={(id, text) => updateItems('hooks', items => items.map(i => i.id === id ? { ...i, text } : i))}
        onRemove={(id) => updateItems('hooks', items => items.filter(i => i.id !== id))}
        onBulkAdd={(newItems) => updateItems('hooks', items => [...items, ...newItems])}
      />

      <ListSection
        title="2nd Pitch - Elaboration"
        description="Deeper value explanation that follows the hook"
        icon={MessageCircleQuestion}
        items={data.elaborations}
        minItems={3}
        placeholder="E.g.: Our AI platform automates the entire outreach process across phone, email, WhatsApp, and LinkedIn, letting your team focus on closing deals instead of chasing leads..."
        sectionKey="elaborations"
        aboutCompany={aboutCompany}
        onAdd={() => updateItems('elaborations', items => [...items, createItem()])}
        onUpdate={(id, text) => updateItems('elaborations', items => items.map(i => i.id === id ? { ...i, text } : i))}
        onRemove={(id) => updateItems('elaborations', items => items.filter(i => i.id !== id))}
        onBulkAdd={(newItems) => updateItems('elaborations', items => [...items, ...newItems])}
      />

      <ListSection
        title="Engaging Questions"
        description="Questions to build rapport and start conversations"
        icon={HelpCircle}
        items={data.engaging_questions}
        minItems={3}
        placeholder="E.g.: What's the biggest bottleneck your sales team faces when trying to scale outreach?"
        sectionKey="engaging_questions"
        aboutCompany={aboutCompany}
        onAdd={() => updateItems('engaging_questions', items => [...items, createItem()])}
        onUpdate={(id, text) => updateItems('engaging_questions', items => items.map(i => i.id === id ? { ...i, text } : i))}
        onRemove={(id) => updateItems('engaging_questions', items => items.filter(i => i.id !== id))}
        onBulkAdd={(newItems) => updateItems('engaging_questions', items => [...items, ...newItems])}
      />

      <ListSection
        title="Qualifying Questions"
        description="Questions to determine if a prospect is a good fit"
        icon={Target}
        items={data.qualifying_questions}
        minItems={2}
        placeholder="E.g.: How many leads does your team typically work with each month, and what's your current conversion rate?"
        sectionKey="qualifying_questions"
        aboutCompany={aboutCompany}
        onAdd={() => updateItems('qualifying_questions', items => [...items, createItem()])}
        onUpdate={(id, text) => updateItems('qualifying_questions', items => items.map(i => i.id === id ? { ...i, text } : i))}
        onRemove={(id) => updateItems('qualifying_questions', items => items.filter(i => i.id !== id))}
        onBulkAdd={(newItems) => updateItems('qualifying_questions', items => [...items, ...newItems])}
      />

      <div className="bg-gradient-to-br from-white to-[#1875FF]/5 rounded-xl border-2 border-[#1875FF]/20 p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:border-[#05E0D1] hover:-translate-y-0.5">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-[#1875FF]/10 to-[#05E0D1]/10 rounded-lg">
              <ShieldAlert className="w-5 h-5 text-[#1875FF]" />
            </div>
            <div>
              <h3 className="text-lg font-extrabold text-gray-900 font-display">Objection Handling</h3>
              <p className="text-sm text-gray-500">Common objections and how to respond (at least 10)</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={generateAllObjections}
              disabled={generatingObjection === 'all' || !hasCompanyInfo}
              title={!hasCompanyInfo ? 'Fill in About Company first' : ''}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#1875FF] to-[#05E0D1] rounded-lg hover:shadow-md transition-all disabled:opacity-50"
            >
              {generatingObjection === 'all' ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4" />
              )}
              {generatingObjection === 'all' ? 'Generating...' : 'AI Generate'}
            </button>
            <button
              onClick={addObjection}
              className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-[#1875FF] bg-[#1875FF]/10 rounded-lg hover:bg-[#1875FF]/20 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add
            </button>
          </div>
        </div>

        <div className="space-y-4">
          {data.objections.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No objections yet. Click "Add" or "AI Generate" to get started.
            </p>
          )}
          {data.objections.map((objection, index) => (
            <div key={objection.id} className="group border border-gray-200 rounded-xl p-4 hover:border-gray-300 transition-colors">
              <div className="flex items-start gap-3">
                <span className="mt-1 text-xs font-medium text-gray-400 w-5 text-right shrink-0">{index + 1}.</span>
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-3">
                    <input
                      type="text"
                      value={objection.title}
                      onChange={(e) => updateObjection(objection.id, { title: e.target.value })}
                      placeholder="E.g.: We don't have the budget right now"
                      className="flex-1 px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm font-medium"
                    />
                    <SimpleCustomSelect
                      value={objection.category}
                      onChange={(val) => updateObjection(objection.id, { category: val as PitchBookObjection['category'] })}
                      options={OBJECTION_CATEGORIES}
                      placeholder="Category"
                      className="w-36"
                    />
                  </div>
                  <div className="relative">
                    <textarea
                      value={objection.response}
                      onChange={(e) => updateObjection(objection.id, { response: e.target.value })}
                      placeholder="How should the agent respond to this objection?"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#05E0D1] focus:border-[#05E0D1] transition-all text-sm resize-none"
                      rows={3}
                    />
                    {objection.is_ai_generated && (
                      <span className="absolute top-2 right-2 flex items-center gap-1 px-2 py-0.5 bg-gradient-to-r from-[#1875FF]/10 to-[#05E0D1]/10 text-[#1875FF] rounded-full text-[10px] font-medium">
                        <Sparkles className="w-2.5 h-2.5" />
                        AI
                      </span>
                    )}
                  </div>
                  {objection.title && (
                    <button
                      onClick={() => generateObjectionResponse(objection.id)}
                      disabled={generatingObjection === objection.id || !hasCompanyInfo}
                      className="flex items-center gap-1.5 text-xs font-medium text-[#1875FF] hover:text-[#1875FF]/80 transition-colors"
                    >
                      {generatingObjection === objection.id ? (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      ) : (
                        <Sparkles className="w-3 h-3" />
                      )}
                      {generatingObjection === objection.id ? 'Generating response...' : 'Generate response with AI'}
                    </button>
                  )}
                </div>
                <button
                  onClick={() => removeObjection(objection.id)}
                  className="mt-1 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg opacity-0 group-hover:opacity-100 transition-all"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
